import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from './PageSEO';

const LAST_UPDATED = 'August 3, 2026';

const SECTIONS = [
  {
    heading: '1. Acceptance of These Terms',
    body: [
      'Welcome to WordZingo ("we", "us", or "our"). These Terms of Use ("Terms") govern your access to and use of the website wordzingo.com and all of the word games, puzzle solvers, and word tools offered on it (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree with any part of these Terms, please do not use the Service.',
    ],
  },
  {
    heading: '2. Use of the Service',
    body: [
      'WordZingo is provided free of charge for personal, non-commercial entertainment and educational use. You may use the games, solvers, and tools as much as you like, subject to these Terms. You agree to use the Service only for lawful purposes and in a way that does not infringe the rights of, restrict, or inhibit anyone else’s use and enjoyment of it.',
    ],
    list: [
      ['No abuse or disruption', 'You may not attempt to interfere with, overload, or disrupt the Service, its servers, or its networks — including through automated scraping, denial-of-service attacks, or bypassing rate limits.'],
      ['No unauthorized access', 'You may not attempt to gain unauthorized access to any part of the Service, other accounts, or connected systems, or probe, scan, or test the vulnerability of the Service.'],
      ['No misuse of content', 'You may not copy, reproduce, or redistribute substantial portions of the Service or its content for commercial purposes without our written permission.'],
    ],
  },
  {
    heading: '3. No Account Required',
    body: [
      'WordZingo does not require you to create an account, provide an email address, or register to use the Service. Any progress, streaks, or saved words are stored locally in your own browser. Because there is no account, you are responsible for any device or browser you use to access the Service.',
    ],
  },
  {
    heading: '4. Intellectual Property',
    body: [
      'The Service — including its design, text, graphics, logos, and the underlying software for the games and solvers — is owned by WordZingo or its licensors and is protected by copyright and other intellectual property laws. The WordZingo name and branding may not be used without our prior written consent.',
      'Some games on WordZingo are inspired by well-known word puzzle formats. Trademarks such as Wordle, Scrabble, Wordfeud, Connections, Spelling Bee, and Letter Boxed are the property of their respective owners. WordZingo is an independent project and is not affiliated with, endorsed by, or sponsored by any of these owners. Our games and solvers are original implementations provided for entertainment and educational purposes.',
    ],
  },
  {
    heading: '5. Advertising',
    body: [
      'WordZingo is free to use and is supported by advertising, including ads served through Google AdSense. By using the Service you acknowledge that ads may be displayed. Your interactions with advertisers, and any dealings with third parties reached through ads, are solely between you and that third party. Please review our Privacy Policy for details on advertising cookies and your choices.',
    ],
  },
  {
    heading: '6. Third-Party Links & Services',
    body: [
      'The Service may contain links to third-party websites or rely on third-party services (such as analytics, advertising, and content delivery providers). We do not control and are not responsible for the content, policies, or practices of any third-party sites or services. Accessing them is at your own risk, and they are governed by their own terms and privacy policies.',
    ],
  },
  {
    heading: '7. Disclaimer of Warranties',
    body: [
      'The Service is provided on an "as is" and "as available" basis, without warranties of any kind, whether express or implied. While we work hard to keep the games and solvers accurate and available, we do not warrant that the Service will be uninterrupted, error-free, or completely secure, or that solver results and word lists will be complete or suitable for competitive play. You use the Service at your own discretion and risk.',
    ],
  },
  {
    heading: '8. Limitation of Liability',
    body: [
      'To the fullest extent permitted by law, WordZingo and its operators will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, or goodwill, arising out of or related to your use of — or inability to use — the Service. Because the Service is provided free of charge, our total liability to you for any claim relating to the Service is limited to the amount you paid to use it, which is zero.',
    ],
  },
  {
    heading: '9. Changes to the Service & Terms',
    body: [
      'We may add, change, or remove games, tools, and features at any time, and we may update these Terms from time to time to reflect changes to the Service or for legal reasons. When we do, we will revise the "Last updated" date at the top of this page. Your continued use of the Service after changes take effect constitutes your acceptance of the revised Terms.',
    ],
  },
  {
    heading: '10. Contact Us',
    body: [
      'If you have any questions about these Terms of Use, please reach out to us through the contact options on wordzingo.com.',
    ],
  },
];

export default function TermsOfUse() {
  return (
    <div>
      <PageSEO page="terms" />

      {/* ── Hero ── */}
      <section style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
        color: '#fff',
        padding: '4rem 1rem 3rem',
        textAlign: 'center',
      }}>
        <div className="container" style={{ maxWidth: 680 }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📜</div>
          <h1 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 2.8rem)', marginBottom: '1rem', letterSpacing: '-0.02em' }}>
            Terms of Use
          </h1>
          <p style={{ fontSize: '1.05rem', opacity: 0.88, lineHeight: 1.7, margin: 0 }}>
            The simple rules for using WordZingo — what you can expect from us, and
            what we ask of you, in plain English.
          </p>
          <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '1rem' }}>
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* ── Body ── */}
      <section style={{ padding: '3rem 1rem' }}>
        <div className="container" style={{ maxWidth: 760 }}>
          {SECTIONS.map(({ heading, body, list }) => (
            <div key={heading} style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(1.25rem, 3.5vw, 1.6rem)', color: '#1e293b', marginBottom: '0.75rem' }}>
                {heading}
              </h2>
              {body.map((p, i) => (
                <p key={i} style={{ fontSize: '1rem', color: '#374151', lineHeight: 1.8, marginBottom: '0.85rem' }}>{p}</p>
              ))}
              {list && (
                <ul style={{ paddingLeft: '1.1rem', margin: '0.25rem 0 0.5rem' }}>
                  {list.map(([term, desc]) => (
                    <li key={term} style={{ fontSize: '1rem', color: '#374151', lineHeight: 1.8, marginBottom: '0.6rem' }}>
                      <strong style={{ color: '#1e293b' }}>{term}:</strong> {desc}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div style={{ borderTop: '2px solid var(--border)', paddingTop: '1.75rem', marginTop: '0.5rem', textAlign: 'center' }}>
            <Link to="/" className="btn fw-bold"
              style={{ borderRadius: 50, background: 'var(--primary)', color: '#fff', border: 'none', fontFamily: 'Nunito, sans-serif', padding: '0.65rem 2rem', fontSize: '1rem' }}>
              🎮 Back to Games
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
